import type { WaterSavedMonthlySummary } from '@metron/shared';
import type { MetricHistoryDataPoint } from '../hooks/useMetricHistory.ts';

/**
 * Company KPI row (MET-3620): five headline numbers for leadership at the top
 * of the Home tab. These helpers combine existing series; no new metrics.
 */

/**
 * Sum of two headline values. A missing side yields '—', never the other
 * side alone: a confident partial total on a KPI slide is worse than a blank.
 */
export function sumOrBlank(
  a: number | null | undefined,
  b: number | null | undefined,
): number | '—' {
  return a == null || b == null ? '—' : a + b;
}

const utcDay = (timestamp: number): string => new Date(timestamp).toISOString().slice(0, 10);

/**
 * Day-aligned sum of two daily series, oldest first. Only days both series
 * cover are kept, so a younger series (e.g. Plus users, history from
 * 2026-09-09) never shows up as a dip in the combined trend.
 */
export function sumByDay(a: MetricHistoryDataPoint[], b: MetricHistoryDataPoint[]): number[] {
  const bByDay = new Map(b.map((p) => [utcDay(p.timestamp), p.value]));
  return [...a]
    .sort((x, y) => x.timestamp - y.timestamp)
    .flatMap((p) => {
      const other = bByDay.get(utcDay(p.timestamp));
      return other === undefined ? [] : [p.value + other];
    });
}

export interface WaterSavedKpis {
  /** Newest computed month (YYYY-MM), not the calendar's last month. */
  lastMonth: { month: string; value: number } | null;
  /** Up to 12 monthly totals, oldest first. */
  monthlySpark: number[];
  /** Sum over the newest month's calendar year, through that month. */
  ytd: { year: string; throughMonth: string; value: number } | null;
  /** Running YTD total per month, oldest first; its last point is the headline. */
  ytdSpark: number[];
}

/**
 * Last-month and YTD gallons saved from the monthly summaries, in any order
 * (`GET /water-saved/monthly` returns them newest first). YTD sums the monthly rows
 * rather than reading the yearly rollup so both cards share one source and
 * the YTD sparkline ends exactly at its headline; the rollup excludes test
 * customers and differs by ~0.1%.
 */
export function waterSavedKpis(months: WaterSavedMonthlySummary[]): WaterSavedKpis {
  // The API sorts newest first, but that contract lives in SQL; sort here so
  // the helper stands on its own. YYYY-MM strings sort chronologically.
  const oldestFirst = [...months].sort((a, b) => a.month.localeCompare(b.month));
  const newest = oldestFirst[oldestFirst.length - 1];
  if (!newest) return { lastMonth: null, monthlySpark: [], ytd: null, ytdSpark: [] };

  const year = newest.month.slice(0, 4);
  let running = 0;
  const ytdSpark = oldestFirst
    .filter((m) => m.month.startsWith(year))
    .map((m) => (running += m.totalGallonsSaved));

  return {
    lastMonth: { month: newest.month, value: newest.totalGallonsSaved },
    monthlySpark: oldestFirst.slice(-12).map((m) => m.totalGallonsSaved),
    ytd: { year, throughMonth: newest.month, value: running },
    ytdSpark,
  };
}
